import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { ELEVATEOS_LOGO_ALT } from '@/constants'

import API from '../../api/api'
import { setTicketData } from '../../reducers/parkingReducer'

import ParkingVehicleInfo from './ParkingVehicleInfo'
import ParkingServicesSummary from './ParkingServicesSummary'
import {
  StatusBadgeInProgress,
  StatusBadgeReadyForPickup,
  StatusBadgeRequested,
  StatusBadgeServiceCompleted,
} from './components/StatusBadge'
import PayBadge from './components/PayBadge'

const ParkingSummary = () => {
  const { t } = useTranslation(['parking'])
  const { ticketNumber } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const ticketData = useSelector((state) => state.parking.ticketData)

  useEffect(() => {
    const fetchTicket = async () => {
      if (Object.keys(ticketData).length === 0 || ticketData.status === 'checked-in') {
        try {
          const resp = await API.getParkingTicket(ticketNumber)
          if (resp.status === 200) {
            dispatch(setTicketData(resp.data))
          }
        } catch {
          navigate('/parking/error/404')
        }
      }
    }

    fetchTicket()
  }, [ticketNumber])

  const vehicleData = {
    plate: ticketData.licensePlate,
    color: ticketData.color,
    make: ticketData.make,
    model: ticketData.model,
    phone: ticketData.phone,
  }

  const ticketSummary = {
    startTime: ticketData.startDate,
    ticketNumber: ticketData.ticketNumber,
    clientName: ticketData.clientName,
    cost: ticketData.serviceFee || 0,
    endTime: ticketData.endDate || new Date(),
    tip: ticketData.tip,
    buildingId: ticketData.buildingId,
    parkingBalance: ticketData.parkingBalance,
    amountPaid: ticketData.amountPaid,
    paidByResident: ticketData.amountPaid,
  }

  const getStatusBadge = () => {
    switch (ticketData.status) {
      case 'requested':
        return <StatusBadgeRequested />
      case 'in-progress':
        return <StatusBadgeInProgress />
      case 'arrived':
        return <StatusBadgeReadyForPickup />
      case 'checked-out':
        return <StatusBadgeServiceCompleted ticketNumber={ticketData.ticketNumber} />
      default:
        return null
    }
  }

  return (
    <div className="parking-info">
      <div className="parking-info__header">
        <img
          className="chitown-logo parking-info__header-logo"
          src="/images/logo/elevateos-logo-wide-black.svg"
          alt={ELEVATEOS_LOGO_ALT}
        />
      </div>
      <div className="badge-container">{getStatusBadge()}</div>
      {ticketData.status !== 'checked-out' && (
        <>
          <ParkingVehicleInfo vehicleData={vehicleData} />
          <ParkingServicesSummary
            ticketSummary={ticketSummary}
            withTip={!!ticketData.tip}
            // Note: If ticket is already paid we show only total payment amount and skip showing paid by resident
            showPaidByResident={!ticketData.paid}
          />
          {!ticketData.paid && <PayBadge ticketNumber={ticketNumber} />}
        </>
      )}
    </div>
  )
}

export default ParkingSummary
